import { ValidationResult } from "react-aria-components";
import {
  useController,
  type Control,
  type FieldPathByValue,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";
import {
  CustomAriaNumberField,
  type CustomAriaNumberFieldProps,
} from "./CustomAriaNumberField";
import { Description, FieldError, FieldProps, Input, Label } from "./Field";
import { composeTailwindRenderProps, focusRing } from "./utils";

export interface NumberFieldProps<T extends object>
  extends Omit<CustomAriaNumberFieldProps, "name">, Pick<FieldProps, "theme"> {
  label?: string;
  description?: string;
  placeholder?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  control?: Control<T>;
  name?: FieldPathByValue<T, number | null>;
  type?: "text" | "hidden";
  size?: "sm" | "md";
  suffix?: React.ReactNode;
  inputClassName?: string;
}

export function NumberField<T extends object>({
  label,
  description,
  control,
  name,
  theme,
  type = "text",
  size = "md",
  suffix,
  inputClassName,
  ...props
}: NumberFieldProps<T>) {
  if (!name || !control) {
    return (
      <CustomAriaNumberField
        {...props}
        className={twMerge("group flex flex-col gap-1", props.className)}
        validationBehavior="aria"
      >
        {label && <Label>{label}</Label>}
        <Input
          size={size}
          type={type}
          placeholder={props.placeholder}
          className={composeTailwindRenderProps(
            focusRing,
            twMerge("text-right placeholder:text-left", inputClassName),
          )}
          suffix={suffix}
          theme={theme}
        />
        {description && <Description>{description}</Description>}
      </CustomAriaNumberField>
    );
  }
  const {
    field,
    fieldState: { error, invalid },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useController({
    control: control,
    name: name,
  });

  return (
    <CustomAriaNumberField
      {...props}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      className={twMerge("group flex flex-col gap-1", props.className)}
      validationBehavior="aria"
      isInvalid={invalid}
    >
      {label && <Label>{label}</Label>}
      <Input
        size={size}
        ref={field.ref}
        type={type}
        placeholder={props.placeholder}
        className={composeTailwindRenderProps(
          focusRing,
          twMerge("text-right placeholder:text-left", inputClassName),
        )}
        suffix={suffix}
        theme={theme}
        showErrorIcon={theme === "stacked" && !!error?.message}
        errorIconTooltip={error?.message}
      />
      {description && <Description>{description}</Description>}
      {theme !== "stacked" && (
        <FieldError className="mt-2 ml-[5px]">
          {error?.message || props.errorMessage}
        </FieldError>
      )}
    </CustomAriaNumberField>
  );
}
